import { useId } from 'react';
import type { JSX, KeyboardEvent } from 'react';

/**
 * The one text field in the app — a port of
 * `android/.../ui/components/NeonTextField.kt` (the colours) together with the
 * `OutlinedTextField` that consumes them in `ui/keyentry/KeyEntryScreen.kt`
 * (the shape, the floating label, the error slot and the IME action).
 *
 * ### Why the resting border is steel and not a faint cyan
 *
 * `--border-interactive`, straight from `neonTextFieldColors()`'s
 * `unfocusedBorderColor = colorScheme.outline`. A "subtle neon border" is the
 * obvious thing to reach for on a screen like this one, and Android's comment
 * records what it costs: cyan at low alpha composites to **2.60:1** over a dark
 * fill and fails WCAG 1.4.11, so the field stops having a visible edge in
 * daylight — which is exactly the condition under which somebody is typing a
 * device key into a phone in the field. Neon appears on focus
 * (`--neon-border-focus`, 55%) and in the caret, and nowhere else.
 *
 * Android's `DecorContrastTest` measures both states with their alpha
 * composited so this cannot quietly regress there. There is no equivalent here,
 * so the tokens are inherited rather than re-picked; see the note at the top of
 * `src/theme/tokens.css`.
 *
 * The DOM shape below is fixed by `components.css` — the label is a *sibling*
 * of the input, not a parent, because `:placeholder-shown + label` is the only
 * pure-CSS way to tell "empty" from "has text" and an adjacent-sibling selector
 * cannot look backwards.
 */
export function NeonTextField({
  value,
  onChange,
  label,
  error,
  disabled = false,
  onSubmit,
}: {
  value: string;
  onChange(v: string): void;
  label: string;
  error?: string | null;
  disabled?: boolean;
  onSubmit?(): void;
}): JSX.Element {
  // Generated rather than taken from the caller. The id exists only so the
  // label's `for=` and the error's `aria-describedby` can point at the input,
  // and both of those are written here — nothing outside the field has ever
  // needed to address it, and `useId` is collision-free across renders and
  // across two fields on one screen.
  const inputId = useId();
  const supportId = `${inputId}-support`;
  const hasError = typeof error === 'string' && error !== '';

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key !== 'Enter' || onSubmit === undefined) return;
    // A pending IME composition is not a submission. Enter commits the
    // candidate for someone typing on the Chinese keyboard, and firing the
    // request on that keystroke would submit a half-finished value and eat the
    // commit. Costs nothing on a Latin keyboard, where `isComposing` is false.
    if (event.nativeEvent.isComposing) return;
    // The field is not inside a <form>, so there is no implicit submission to
    // suppress today — but a page navigation in a WebClip unmounts the app and
    // kills the cycle in flight, and that is not a failure mode worth leaving
    // one refactor away.
    event.preventDefault();
    onSubmit();
  }

  return (
    <div className={hasError ? 'neon-field neon-field--error' : 'neon-field'}>
      <div className="neon-field__box">
        <input
          id={inputId}
          className="neon-field__input"
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          // Load-bearing, not cosmetic: `:placeholder-shown` is what floats the
          // label, and it only matches while a placeholder is actually being
          // shown. A single space is the neutral value — the field shows a
          // floating label, never placeholder text, so there is nothing else it
          // could say and no caller has ever wanted to fill it.
          placeholder=" "
          // Android reaches this state through `KeyboardOptions(imeAction =
          // ImeAction.Done)`; `enterKeyHint` is the web spelling of the same
          // thing and is what puts "done" on the iOS return key.
          enterKeyHint="done"
          // A device key is an opaque ASCII token. Every one of these off by
          // default would let iOS capitalise its first letter, autocorrect it
          // into a dictionary word, or offer it back from a saved-password
          // prompt — and the key is never supposed to be stored anywhere but
          // the icon's URL (SPEC §13).
          autoCapitalize="none"
          autoCorrect="off"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={hasError ? true : undefined}
          aria-describedby={hasError ? supportId : undefined}
        />
        {/* After the input in source order — see the sibling-selector note above. */}
        <label className="neon-field__label" htmlFor={inputId}>
          {label}
        </label>
      </div>
      {hasError ? (
        <p id={supportId} className="neon-field__support t-body-small">
          {error}
        </p>
      ) : null}
    </div>
  );
}
